import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { AHeader, ACard, ATable, inputDark } from "@/components/shared/AdminKit";
import { fmtDateShort } from "@/components/shared/DashKit";
import { Loader2, Search, Download } from "lucide-react";

export default function AdminAccountDeletions() {
  const { t } = useContent();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [accountType, setAccountType] = useState("");
  const [result, setResult] = useState("");

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (accountType) p.set("account_type", accountType);
    if (result) p.set("result", result);
    api.get(`/admin/account-deletions?${p.toString()}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [q, accountType, result]);

  const exportCsv = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (accountType) p.set("account_type", accountType);
    if (result) p.set("result", result);
    try {
      const r = await api.get(`/admin/account-deletions/export?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url; link.download = "account_deletions.csv";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* no-op: RBAC/handled upstream */ }
  };

  const resultBadge = (action) => action === "ACCOUNT_DELETE"
    ? <span className="rounded bg-red-500/15 px-2 py-0.5 text-xs text-red-400">{t("admin.deletions.result.deleted", "Supprimé")}</span>
    : <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs text-amber-400">{t("admin.deletions.result.blocked", "Bloqué")}</span>;

  return (
    <div data-testid="account-deletions-page">
      <AHeader title={t("admin.deletions.title", "Journal des suppressions")} subtitle={t("admin.deletions.subtitle", "Historique en lecture seule des suppressions de comptes (basé sur les journaux d'audit).")} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 lg:w-80">
          <Search className="h-4 w-4 text-slate-500" />
          <input data-testid="deletions-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.deletions.search", "Rechercher (ID cible ou admin)...")} className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-slate-500" />
        </div>
        <select data-testid="deletions-type" value={accountType} onChange={(e) => setAccountType(e.target.value)} className={inputDark + " lg:w-48"}>
          <option value="">{t("admin.deletions.type_all", "Tous les types")}</option>
          <option value="customer">Client</option>
          <option value="agent">Agent</option>
          <option value="admin">Admin</option>
        </select>
        <select data-testid="deletions-result" value={result} onChange={(e) => setResult(e.target.value)} className={inputDark + " lg:w-48"}>
          <option value="">{t("admin.deletions.result_all", "Tous les résultats")}</option>
          <option value="deleted">{t("admin.deletions.result.deleted", "Supprimé")}</option>
          <option value="blocked">{t("admin.deletions.result.blocked", "Bloqué")}</option>
        </select>
        <button onClick={exportCsv} data-testid="deletions-export" className="ml-auto flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700">
          <Download className="h-4 w-4" /> {t("admin.deletions.export", "Exporter CSV")}
        </button>
      </div>
      <ACard>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : data.items.length === 0 ? <div className="py-8 text-center text-sm text-slate-500" data-testid="deletions-empty">{t("admin.deletions.empty", "Aucune suppression enregistrée.")}</div>
            : (
              <ATable head={[t("admin.deletions.col.date", "Date"), t("admin.deletions.col.admin", "Admin"), t("admin.deletions.col.target", "Compte cible"), t("admin.deletions.col.type", "Type"), t("admin.deletions.col.result", "Résultat"), t("admin.deletions.col.detail", "Détail")]}>
                {data.items.map((a, i) => (
                  <tr key={i} className="border-b border-slate-800/60 last:border-0" data-testid={`deletion-row-${i}`}>
                    <td className="px-3 py-3 text-slate-400">{fmtDateShort(a.created_at)}</td>
                    <td className="px-3 py-3">{a.user_email || a.user_id || "—"}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-300">{a.entity_id}</td>
                    <td className="px-3 py-3"><span className="rounded bg-slate-800 px-2 py-0.5 text-xs capitalize">{a.metadata?.account_type || "—"}</span></td>
                    <td className="px-3 py-3">{resultBadge(a.action)}</td>
                    <td className="px-3 py-3 text-xs text-slate-400">
                      {a.action === "account_delete_blocked"
                        ? (a.metadata?.blockers || []).map((c) => t(`admin.users.delete.blocker.${c}`, c)).join(", ")
                        : t("admin.deletions.anonymized", "Anonymisé (données historiques conservées)")}
                    </td>
                  </tr>
                ))}
              </ATable>
            )}
      </ACard>
    </div>
  );
}
